import React, { useState } from "react";
import { InputForm, SubmitButton, Error } from "components";
import { CreateVideoError, CreateVideoInput } from "../../store/actionType";
import { CloudUploadIcon } from "assets";

interface FormProps {
  loading: boolean;
  header: string;
  formData: CreateVideoInput;
  videoError: CreateVideoError;
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  handleVideoFileChange?: (file: File) => void;
  isNewVideo?: boolean;
  posterUrl?: string | null;
}
const VideoForm: React.FC<FormProps> = ({
  formData,
  handleChange,
  handleSubmit,
  videoError,
  loading,
  header,
  handleVideoFileChange,
  isNewVideo,
  posterUrl,
}) => {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
      if (handleVideoFileChange) handleVideoFileChange(file);
    }
  };
  return (
    <form>
      <h2 className="text-xl font-bold my-3">{header}</h2>

      {/* Title Input */}
      <div className="pb-3">
        <InputForm
          label="Title"
          type="text"
          id="title"
          name="title"
          value={formData.title ?? ""}
          onChange={handleChange}
        />
        {videoError.title && <Error>{videoError.title}</Error>}
      </div>

      {/* File Upload Section */}
      {isNewVideo && (
        <>
          {videoPreviewUrl ? (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Video Preview:
              </p>
              <video
                controls
                src={videoPreviewUrl}
                className="w-full max-h-96 rounded-md border border-gray-300"
              />
            </div>
          ) : (
            <div
              id="Upload Video"
              className="flex items-center justify-center w-full mb-4"
            >
              <label
                htmlFor="dropzone-file"
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    setVideoFile(file);
                    setVideoPreviewUrl(URL.createObjectURL(file));
                    if (handleVideoFileChange) handleVideoFileChange(file);
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="relative flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
              >
                {posterUrl ? (
                  <>
                    <img
                      src={posterUrl}
                      alt="Video Poster"
                      className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-70"
                    />
                    <div className="z-10 flex flex-col items-center justify-center">
                      <CloudUploadIcon />
                      <p className="mb-2 text-sm text-white">
                        <span className="font-semibold">Click to upload</span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-white">
                        MP4, WebM, or other video formats
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <CloudUploadIcon />
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      MP4, WebM, or other video formats
                    </p>
                  </div>
                )}
                <input
                  id="dropzone-file"
                  type="file"
                  className="hidden"
                  accept="video/*"
                  onChange={handleFileChange}
                />
                {videoError.video && <Error>{videoError.video}</Error>}
              </label>
            </div>
          )}
        </>
      )}
      {/* Submit Button */}
      <SubmitButton loading={loading} onClick={handleSubmit} />
    </form>
  );
};

export default VideoForm;
